/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { GeoJsonObject } from 'geojson';
import { FeatureGroup, Marker, Polyline } from 'leaflet';
import { DrawenLayerResultType } from 'src/app/helpers/drawenLayer.helper';
import { EEnvironmentalUnitType } from 'src/enums/mixed.enum';
import { EnvironmentalUnit, MapUnit } from 'src/types/unit.type';
import { EMarkerDisplayMethod } from './MapMarkerController';

type LabelRecord = {
  marker: L.Marker;
  baseSize: number;
  baseZoom: number;
};

export type TCustomColorPicker =
  | ({
      options: {
        position: string;
      };
      onAdd(): HTMLDivElement;
    } & L.Control)
  | undefined;

export type TCustomDrawGroupPicker =
  | ({
      options: {
        position: string;
      };
      onAdd(): HTMLDivElement;
    } & L.Control)
  | undefined;

export type ControllerLayersContainerValue = {
  count: number;
  includedByGroup: number[];
  dbEntry: DrawenLayerResultType;
};

export type ControllerLayersContainer = Record<string, ControllerLayersContainerValue>;

export type TActiveUnitFilters = {
  sides: number[];
  factions: number[];
  prototypes: number[];
};

export type TMarkersContainer = Record<
  number,
  {
    unit: MapUnit;
    marker: Marker;
    remainingRoute: Polyline;
    previousRoute: Polyline;
    isLabelVisible: boolean;
    markerDisplayMethod: EMarkerDisplayMethod;
    unitStandbyArea: L.FeatureGroup;
    reconnaisanceZones: L.FeatureGroup;
    patrolReconZone: L.FeatureGroup;
    capControlZone: L.FeatureGroup;
  }
>;

export const drawingOptions = (drawColor: string) => {
  return {
    polyline: {
      drawError: {
        message: 'Failed to draw polyine',
      },
      showLength: true,
      shapeOptions: {
        color: drawColor,
      },
    },
    polygon: {
      allowIntersection: false,
      drawError: {
        message: 'Failed to draw polygon',
      },
      showArea: true,
      shapeOptions: {
        color: drawColor,
      },
    },
    circle: {
      showRadius: true,
      shapeOptions: {
        color: drawColor,
      },
    },
    circlemarker: {
      color: drawColor,
    },
    rectangle: {
      shapeOptions: {
        color: drawColor,
      },
    },
  };
};

export const layerToGeojson = (layer: any) => {
  const geoJson = layer.toGeoJSON();
  geoJson.properties.style = {
    color: layer.options.color,
    fillColor: layer.options.fillColor,
    opacity: layer.options.opacity,
    fillOpacity: layer.options.fillOpacity,
    dashArray: layer.options?.dashArray,
  };

  if (layer._mRadius) {
    geoJson.properties.radius = layer._mRadius;
  }

  if (layer._radius && !layer._mRadius) {
    geoJson.properties.radius = layer._radius;
  }

  // if (layer instanceof L.Rectangle) {
  //   geoJson.geometry.type = 'Rectangle';
  // }

  return geoJson;
};

const getTooltipLabel = (opts: { text: string; color: string; fontSize: number }) => {
  const span = document.createElement('span');
  span.setAttribute('style', `color:${opts.color}; font-size:${opts.fontSize}px`);
  span.innerHTML = opts.text;
  return span;
};

export function dbDataToGeojsonLayer(
  map: L.Map,
  features: GeoJsonObject | undefined,
  onEachFeature?: (feature: any, layer: L.Layer) => void,
): L.GeoJSON {
  const labels: LabelRecord[] = [];

  const layer = L.geoJSON(features, {
    uuid: (features as any)?.properties?.uuid,

    pointToLayer: (feature, latlng) => {
      if (feature.properties?.radius != null) {
        const markerColor = feature.properties?.style?.color || feature.properties?.color || '#262628';
        const opacity = feature.properties?.style?.opacity || feature.properties?.opacity || 1;
        if (feature.properties.radius === 10) {
          return L.marker(latlng, {
            icon: L.divIcon({
              className: 'draw-circlemarker-svg-icon',
              html: `<svg width="30" height="30" viewBox="0 0 0.75 0.75" xmlns="http://www.w3.org/2000/svg"><path d="M.356 0a.02.02 0 0 0-.019.019V.6a.019.019 0 1 0 .037 0V.276L.662.168a.019.019 0 0 0 0-.035L.375.024V.019A.02.02 0 0 0 .356 0m.019.065L.603.15.375.235zM.3.452a.7.7 0 0 0-.189.037Q.062.507.033.534C.004.561 0 .575 0 .6s.013.047.033.065q.03.027.078.045c.064.025.15.04.245.04S.537.735.601.71Q.65.692.679.665C.708.638.713.625.713.6S.7.553.68.535A.2.2 0 0 0 .602.49.6.6 0 0 0 .413.453V.49q.105.008.176.035.044.018.066.038c.022.02.02.025.02.037S.669.624.655.637.618.664.589.675C.531.698.448.712.357.712S.183.697.125.675Q.081.657.059.637C.037.617.037.612.037.6S.043.576.057.563.094.536.123.525A.6.6 0 0 1 .299.49z" style="fill:${markerColor};fill-opacity:${opacity};stroke:none;stroke-width:0"/></svg>`,
              iconSize: [30, 30],
              iconAnchor: [15, 30],
            }),
          });
        }
        return new L.Circle(latlng, feature.properties.radius);
      }

      if (feature.properties.isLabel) {
        const { text: txt, color: col, fontSize: size, zoomLevel: baseZoom } = feature.properties;

        const svgHtml = `
          <svg width="30" height="30" viewBox="0 0 0.9 0.9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.45 0.787V0.112m0 0.675 0.075 -0.075m-0.075 0.075 -0.075 -0.075m0.075 -0.6 0.075 0.075m-0.075 -0.075 -0.075 0.075m0.412 0.263H0.112m0.675 0 -0.075 -0.075m0.075 0.075 -0.075 0.075M0.112 0.45l0.075 -0.075m-0.075 0.075 0.075 0.075" stroke="#000" stroke-width="0.075" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.3;" /></svg>`.trim();

        const svgIcon = L.divIcon({
          html: svgHtml,
          className: '',
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        const marker = L.marker(latlng, {
          icon: svgIcon,
          opacity: 1,
        });

        marker.bindTooltip(getTooltipLabel({ text: txt, color: col, fontSize: size }), {
          permanent: true,
          className: 'text-marker-tooltip',
          direction: 'center',
          offset: [0, 0],
        });

        labels.push({ marker, baseSize: size, baseZoom });
        return marker;
      }

      return new L.Marker(latlng);
    },

    style: (feature) => ({
      fillColor: feature?.properties.style?.fillColor,
      color: feature?.properties.style?.color,
      opacity: feature?.properties.style?.opacity,
      fillOpacity: feature?.properties.style?.fillOpacity,
      dashArray: feature?.properties.style?.dashArray,
    }),

    onEachFeature: (feature, featureLayer) => {
      if (featureLayer instanceof L.Path) {
        const originalColor = (featureLayer.options.color as string) || '#000';
        const originalFillColor = (featureLayer.options.fillColor as string) || '#000';
        featureLayer.on('mouseover', () => {
          featureLayer.setStyle({
            color: 'var(--ete-primary-green-color)',
            fillColor: 'var(--ete-primary-green-color)',
          });
        });
        featureLayer.on('mouseout', () => {
          featureLayer.setStyle({ color: originalColor, fillColor: originalFillColor });
        });
      }

      if (onEachFeature) {
        onEachFeature(feature, featureLayer);
      }
    },
  }).addTo(map);

  const updateLabelSizes = () => {
    const zoom = map.getZoom();
    labels.forEach(({ marker, baseSize, baseZoom }) => {
      const newFontSize = (baseSize * zoom) / baseZoom;
      const tooltip = marker.getTooltip();
      if (tooltip) {
        // regenerate the tooltip contents with the new size
        const props = (marker as any).feature.properties;
        tooltip.setContent(
          getTooltipLabel({
            text: props.text,
            color: props.color,
            fontSize: newFontSize,
          }),
        );
      }
    });
  };
  map.on('zoomend', updateLabelSizes);
  updateLabelSizes();

  return layer;
}

export type TEnvUnitsContainer = Record<
  number,
  | { envUnit: EnvironmentalUnit; layer: L.CustomConic; layerType: EEnvironmentalUnitType.CIRCLE }
  | { envUnit: EnvironmentalUnit; layer: L.Polygon; layerType: EEnvironmentalUnitType.POLYGON }
>;

export const getDefaultReconZone = (zoneId: string) => {
  return new L.Rectangle(
    [
      [0, 0],
      [0, 0],
    ],
    {
      // @ts-ignore
      imgId: 'pattern',
      zoneId,
    },
  );
};

export const createOrGetZone = (featureGroup: FeatureGroup, zoneId: string) => {
  const layers = featureGroup.getLayers() as L.Rectangle[];

  let layer = layers.find((o) => o?.options.zoneId === zoneId);

  if (!layer) {
    layer = getDefaultReconZone(zoneId);
    featureGroup.addLayer(layer);
  }

  return layer;
};

export const registerLayer = (
  container: ControllerLayersContainer,
  dbEntry: DrawenLayerResultType,
  uuid: string,
  groupId: number,
  activeGroupIds: number[],
) => {
  // Check if layer is already registered
  if (container[uuid]) {
    if (!container[uuid].includedByGroup.includes(groupId)) {
      // If layer is not being included by the same group again

      // Add new groupsId to list
      container[uuid].includedByGroup.push(groupId);

      // Increase counter
      container[uuid].count += 1;
    }

    // Because layer is already registered, no need to redraw
    return { registerInfo: container[uuid], shouldDrawLayer: false };
  }

  // If layer is not included earlier, create new entry and return true for drawing
  container[uuid] = { count: 1, dbEntry, includedByGroup: [groupId] };
  return { registerInfo: container[uuid], shouldDrawLayer: activeGroupIds.includes(groupId) };
};

export const unregisterLayer = (
  container: ControllerLayersContainer,
  uuid: string,
  groupId: number,
): { shouldRemoveLayer: false } | { shouldRemoveLayer: true; entity: ControllerLayersContainerValue } => {
  // Check if layer entry existing and groupId exists on includedByGroup list
  if (container[uuid] && container[uuid].includedByGroup.includes(groupId)) {
    if (container[uuid].count > 1) {
      // If layer is added by multiple groups

      // Decrease counter
      container[uuid].count -= 1;

      // Remove group from the list
      container[uuid].includedByGroup = container[uuid].includedByGroup.filter((o) => o !== groupId);

      // Because layer is included by other groups, layer should stay
      return { shouldRemoveLayer: false };
    }

    if (container[uuid].count === 1) {
      // If layer is not included by any other group,, return true for removing
      return { entity: container[uuid], shouldRemoveLayer: true };
    }
  }
  return { shouldRemoveLayer: false };
};

export const unregisterLayersByGroupId = (container: ControllerLayersContainer, groupId: number) => {
  const entries = Object.entries(container)
    .filter(([, entry]) => entry.includedByGroup.includes(groupId))
    .map(([uuid, entry]) => {
      return { uuid, entry };
    });
  return entries.map((entry) => {
    return { ...unregisterLayer(container, entry.uuid, groupId), uuid: entry.uuid };
  });
};
